import EmptyWorkspace from '@/components/layout/EmptyWorkspace';
import MarketList from '@/components/modules/MarketList';
import AccountValue from '@/components/trader-modules/AccountValue';
import { useAppSelector } from '@/state/hooks';
import {
    selectSelectedXplorTraderWorkspaceTab,
    selectXplorTraderWorkspaceTabs,
    XplorTraderWorkspace
} from '@/state/reducers/workspaceSlice';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    MdOutlineAccountBalanceWallet,
    MdOutlineCandlestickChart,
    MdOutlineHistory,
    MdOutlinePivotTableChart,
    MdOutlinePriceChange,
    MdOutlineSwapVerticalCircle
} from 'react-icons/md';
import { MosaicNode } from 'react-mosaic-component';
import { v4 as uuidv4 } from 'uuid';
import useLanguage from './useLanguage';
import { ConfigType } from './useXplorSpotLayout';

export type XplorTraderModules =
    | 'Init'
    | 'Tickers'
    | 'Account Value'
    | 'Blotter'
    | 'Markets'
    | 'Order Book'
    | 'Chart'
    | '';
export const xplorTraderTabList = ['Tickers', 'Account Value', 'Blotter', 'Markets', 'Order Book', 'Chart'];
export const xplorTraderTabIcon = {
    Tickers: MdOutlineSwapVerticalCircle,
    Blotter: MdOutlineHistory,
    'Account Value': MdOutlineAccountBalanceWallet,
    Chart: MdOutlineCandlestickChart,
    Markets: MdOutlinePivotTableChart,
    'Order Book': MdOutlinePriceChange
} as Record<XplorTraderModules, IconType>;

export const getDesktopLayout = (): MosaicNode<string> => {
    return {
        direction: 'row',
        first: {
            direction: 'column',
            first: {
                direction: 'row',
                first: `Chart-${uuidv4()}`,
                second: `Tickers-${uuidv4()}`,
                splitPercentage: 45
            },
            second: {
                direction: 'row',
                first: `Order Book-${uuidv4()}`,
                second: `Blotter-${uuidv4()}`,
                splitPercentage: 30
            },
            splitPercentage: 50
        },
        second: {
            direction: 'column',
            first: `Markets-${uuidv4()}`,
            second: `Account Value-${uuidv4()}`,
            splitPercentage: 68
        },
        splitPercentage: 78
    };
};

export const getMobileLayout = (): MosaicNode<string> => {
    return {
        direction: 'row',
        first: `Tickers`,
        second: '',
        splitPercentage: 100
    };
};

export const defaultXplorTraderLayout = () => {
    return {
        mobile: getMobileLayout(),
        desktop: getDesktopLayout()
    };
};

export interface UseXplorTraderLayoutReturn {
    workspace: XplorTraderWorkspace;
    config: ConfigType;
}

const useXplorTraderLayout = (): UseXplorTraderLayoutReturn => {
    const lang = useLanguage();

    const workspaceTab = useAppSelector(selectSelectedXplorTraderWorkspaceTab);
    const workspaceTabs = useAppSelector(selectXplorTraderWorkspaceTabs);

    const workspace = useMemo(() => workspaceTab || workspaceTabs[0], [workspaceTab, workspaceTabs]);

    const config: ConfigType = useMemo(() => {
        return {
            Init: {
                key: 'Init',
                title: '',
                component: (
                    <EmptyWorkspace tabList={xplorTraderTabList} tabIcon={xplorTraderTabIcon} platform="xplortrader" />
                )
            },
            Tickers: {
                key: 'Tickers',
                title: lang.modules.tickers
            },
            Blotter: {
                key: 'Blotter',
                title: lang.modules.blotter
            },
            Chart: {
                key: 'Chart',
                title: lang.modules.chart
            },
            'Account Value': {
                key: 'Account Value',
                title: lang.modules['account-value'],
                component: (
                    <div id="accountValue" className="h-full bg-brand-background">
                        <AccountValue />
                    </div>
                )
            },
            Markets: {
                key: 'Markets',
                title: lang.modules.markets,
                component: (
                    <div id="market-list" className="bg-brand-background h-full max-h-full overflow-hidden">
                        <MarketList isTraderMode />
                    </div>
                )
            },
            'Order Book': {
                key: 'Order Book',
                title: lang.modules['order-book']
            }
        };
    }, [lang]);

    return { workspace, config };
};

export default useXplorTraderLayout;
